import { pure } from '@core/helpers/pure';
import { TuiComboBox } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs';
import {
  TuiDialogContext,
  TuiDataList,
  TuiDropdown,
  TuiButton,
  TuiTextfield,
  TuiLabel,
} from '@taiga-ui/core';
import { TUI_DEFAULT_MATCHER } from '@taiga-ui/cdk';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { TranslatePipe } from '@ngx-translate/core';
import { RequestFiltersService } from '@core/services/request/request-filters.service';
import { Counterparty } from '@core/types';

/** Текущее значение фильтра, передаётся в диалог при открытии */
export interface OwnerFilterDialogData {
  counterpartyOwnerId?: number;
}

/** Результат диалога; пустой объект = «Сбросить» */
export interface OwnerFilterDialogResult {
  counterpartyOwnerId?: number;
}

/**
 * Модалка фильтра «Заказчик» (порт #ownersModal из старой системы):
 * одиночный выбор заказчика-контрагента с поиском по названию.
 */
@Component({
  selector: 'x-owner-filter-dialog',
  imports: [
    TuiTextfield,
    TuiLabel,
    FormsModule,
    TranslatePipe,
    TuiButton,
    TuiComboBox,
    TuiDataList,
    TuiDropdown,
  ],
  templateUrl: './owner-filter-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OwnerFilterDialogComponent implements OnInit {
  protected readonly context = inject(POLYMORPHEUS_CONTEXT) as TuiDialogContext<
    OwnerFilterDialogResult,
    OwnerFilterDialogData | undefined
  >;
  private readonly requestFiltersService = inject(RequestFiltersService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  protected counterparties: Counterparty[] = [];
  protected selectedOwner: Counterparty | null = null;
  protected search: string | null = null;

  protected readonly stringifyFn = ({ name }: { name: string }) => name;

  ngOnInit(): void {
    // Список заказчиков уже загружен списком заявок (initFiltersData)
    this.requestFiltersService.filtersData$
      .pipe(
        map(({ counterparties }) => counterparties),
        filter((counterparties): counterparties is Counterparty[] =>
          Array.isArray(counterparties),
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(counterparties => {
        this.counterparties = counterparties;
        this.selectedOwner =
          this.counterparties.find(
            counterparty =>
              counterparty.id === this.context.data?.counterpartyOwnerId,
          ) ?? null;
        this.cdr.markForCheck();
      });
  }

  @pure
  protected filterCounterparties(
    counterparties: Counterparty[],
    search: string | null,
  ): Counterparty[] {
    if (!search) {
      return counterparties;
    }

    return counterparties.filter(counterparty =>
      TUI_DEFAULT_MATCHER(counterparty.name, search),
    );
  }

  protected apply(): void {
    this.context.completeWith(
      this.selectedOwner ? { counterpartyOwnerId: this.selectedOwner.id } : {},
    );
  }

  protected reset(): void {
    this.context.completeWith({});
  }
}
